"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { postFeedback } from "@/lib/api-client-browser";

interface ArticleFeedbackProps {
  articleId: string;
  slug: string;
}

type FeedbackState = "idle" | "yes" | "no" | "submitted";

const FEEDBACK_REASONS = ["不正確", "不明瞭", "情報不足", "無関係"] as const;
type FeedbackReason = (typeof FEEDBACK_REASONS)[number];

const STORAGE_KEY_PREFIX = "faqrun_feedback_";

export function ArticleFeedback({ articleId, slug }: ArticleFeedbackProps) {
  const [state, setState] = useState<FeedbackState>("idle");
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
  const [comment, setComment] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${articleId}`);
    if (stored === "yes" || stored === "no") {
      setState("submitted");
    }
  }, [articleId]);

  const handleYes = () => {
    setState("yes");
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${articleId}`, "yes");
    // fire-and-forget: API 送信失敗しても UX に影響させない
    postFeedback(slug, true).catch(() => {
      // エラーは無視（localStorage による重複防止は維持）
    });
  };

  const handleNo = () => {
    setState("no");
  };

  const toggleReason = (reason: FeedbackReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason],
    );
  };

  const handleSubmit = () => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${articleId}`, "no");
    setState("submitted");

    const reason = selectedReasons.length > 0 ? selectedReasons.join(",") : undefined;
    const commentValue = comment.trim() || undefined;

    // fire-and-forget: API 送信失敗しても UX に影響させない
    postFeedback(slug, false, reason, commentValue).catch(() => {
      // エラーは無視
    });
  };

  return (
    <section aria-labelledby="feedback-heading">
      <div className="border-y border-[#E5E7EB] py-12 text-center">
        {state === "idle" && (
          <>
            <h2
              id="feedback-heading"
              className="mb-6 text-[18px] font-semibold text-[#111827] md:text-[18px]"
            >
              この記事は役に立ちましたか？
            </h2>
            <div className="flex items-center justify-center gap-3" aria-live="polite">
              <button
                onClick={handleYes}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-6 py-2.5 text-[15px] text-[#374151] transition-colors duration-200 hover:border-[#86EFAC] hover:bg-[#F0FDF4]"
              >
                <span>👍</span>
                <span>はい</span>
              </button>
              <button
                onClick={handleNo}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-6 py-2.5 text-[15px] text-[#374151] transition-colors duration-200 hover:border-[#FCA5A5] hover:bg-[#FEF2F2]"
              >
                <span>👎</span>
                <span>いいえ</span>
              </button>
            </div>
          </>
        )}

        {state === "yes" && (
          <p className="text-[16px] font-medium text-[#059669]" aria-live="polite">
            ✅ ご回答ありがとうございます！
          </p>
        )}

        {state === "submitted" && (
          <p className="text-[16px] font-medium text-[#059669]" aria-live="polite">
            ✅ ご回答ありがとうございます！
          </p>
        )}

        {state === "no" && (
          <div className="mx-auto max-w-lg" aria-live="polite">
            <p className="mb-2 text-[16px] font-semibold text-[#111827]">
              ご回答ありがとうございます。
            </p>
            <p className="mb-6 text-[14px] text-[#6B7280]">具体的にどの点が問題でしたか？</p>

            <div className="mb-6 flex flex-wrap justify-center gap-2">
              {FEEDBACK_REASONS.map((reason) => {
                const isSelected = selectedReasons.includes(reason);
                return (
                  <button
                    key={reason}
                    onClick={() => toggleReason(reason)}
                    className="cursor-pointer rounded-full border px-4 py-1.5 text-[14px] transition-colors duration-200"
                    style={{
                      borderColor: isSelected ? "var(--theme-primary)" : "#D1D5DB",
                      backgroundColor: isSelected
                        ? "color-mix(in srgb, var(--theme-primary) 10%, white)"
                        : "white",
                      color: isSelected ? "var(--theme-primary)" : "#374151",
                    }}
                    aria-pressed={isSelected}
                  >
                    {reason}
                  </button>
                );
              })}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="どんな情報が必要でしたか？（任意）"
              className="mb-4 w-full max-w-[480px] resize-none rounded-lg border border-[#D1D5DB] p-3 text-[14px] text-[#374151] placeholder-[#9CA3AF] transition-colors duration-200 focus:border-[var(--theme-primary)] focus:outline-none"
              style={{ height: "80px" }}
            />

            <div className="mb-6 flex justify-center">
              <button
                onClick={handleSubmit}
                className="cursor-pointer rounded-lg px-6 py-2.5 text-[15px] font-medium text-white transition-opacity duration-200 hover:opacity-80"
                style={{ backgroundColor: "var(--theme-primary)" }}
              >
                送信する
              </button>
            </div>

            <div className="flex items-center justify-center gap-4">
              <div className="h-px flex-1 bg-[#D1D5DB]" style={{ maxWidth: "80px" }} />
              <span className="text-[13px] text-[#D1D5DB]">または</span>
              <div className="h-px flex-1 bg-[#D1D5DB]" style={{ maxWidth: "80px" }} />
            </div>

            <div className="mt-4 flex items-center justify-center gap-6">
              <Link
                href="/search"
                className="text-[14px] transition-opacity duration-200 hover:opacity-80"
                style={{ color: "var(--theme-primary)" }}
              >
                🔍 他の記事を探す
              </Link>
              <span className="text-[14px] text-[#6B7280]">|</span>
              <a
                href="#"
                className="text-[14px] transition-opacity duration-200 hover:opacity-80"
                style={{ color: "var(--theme-primary)" }}
              >
                📝 問い合わせる
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
